import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useTiersGameContext } from "../context/TiersGameContext";
import { Typography, Button, Box } from "@mui/material";
import TiersCard from "../TiersCard/TiersCard";
import IntervalSelectionButtons from "../../GamePageComponents/TabSelectionButtons/IntervalSelectionButtons";
import { useNavigate, useLocation } from "react-router-dom";
import isEqual from "lodash/isEqual";
import QuickPicksSubmitBar from "../../GamePageComponents/SubmitBars/QuickPicksSubmitBar";
import { useSaveTiersPicks } from "../../../hooks/pools/useSaveTiersPicks";
import ResponseModal from "../../GameModals/ResponseModal/ResponseModal";
import TiersSubmitModal from "../../GameModals/TiersModals/TiersSubmitModal";
import { DateTime } from 'luxon';
import { shortDayDateTimFormat } from "../../../utils/DateFormatting/ShortDayDateTimeFormat";
import PickDeadline from "../../QuickPickGames/CardPieces/PickDeadline";
import QuickPickGamesComing from "../../QuickPickGames/CardPieces/QuickPickGamesComing";
import TiersDisplayHandler from "./TiersDisplays/TiersDisplayHandler";
import { useDispatch } from "react-redux";
import { userLogoutAction } from "../../../redux/actions/loginImport";
import { useTheme } from "@emotion/react";
import TiebreakerAdModal from "../../GameModals/TiersModals/TiebreakerAdModal";
import useMediaQuery from "@mui/material/useMediaQuery";

const TiersMyPicks = () => {
    const {
        interval,
        pickState,
        setPickState,
        originalPicks,
        unsavedPicks,
        setUnsavedPicks,
        masterPoolId,
        myUserId,
        pickIndex,
        intervalInfo,
        masterPoolData,
        league,
        leaderboardData,
    } = useUserAndGameContext();

    const {
        tiersData,
        theCurrentInterval,
        intervalOptions,
        oneIntervalEvent,
    } = useTiersGameContext();

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [submitBarOpen, setSubmitBarOpen] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [tiebreakerAdModal, setTiebreakerAdModal] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [checkerData, setCheckerData] = useState(null);

    const { mutate: saveTiersPicks } = useSaveTiersPicks();

    const tiersComingTitle = masterPoolData?.modalInfo?.myPicks?.title;
    const tiersComingBody = masterPoolData?.modalInfo?.myPicks?.body;

    // Count player picks for the current interval - wrapped in useCallback
    const countPlayerPicks = useCallback((playerId) => {
        if (!leaderboardData || !playerId) return 0;

        let pickCount = 0;
        leaderboardData.forEach(user => {
            // Find the matching interval data for this user
            const intervalData = user.intervalData?.find(int => int.interval === interval);

            if (intervalData) {
                // Check if any of this user's picks match the player ID
                const hasPlayerPick = intervalData.players?.some(
                    player => player.id === playerId.toString()
                );

                if (hasPlayerPick) {
                    pickCount++;
                }
            }
        });

        return pickCount;
    }, [leaderboardData, interval]);

    //console.log('League data',league);

    const handleCancel = useCallback(() => {
        setPickState(originalPicks);
        setSubmitBarOpen(false);
    }, [originalPicks, setPickState]);

    const handleSubmit = useCallback(async () => {
        if (hasSubmitted) return;
        setHasSubmitted(true);

        const newPicks = pickState?.filter(pick => !originalPicks?.some(originalPick => isEqual(pick, originalPick)));
        const removedPicks = originalPicks?.filter(originalPick => !pickState?.some(pick => isEqual(originalPick, pick)));

        const pickData = {
            masterPoolId,
            myUserId,
            pickIndex: Number(pickIndex),
            newPicks,
            removedPicks,
            interval: Number(interval),
        };

        try {
            await saveTiersPicks(pickData, {
                onSuccess: (response) => {
                    setUnsavedPicks(false);
                    setSubmitBarOpen(false);
                    setSuccessModalOpen(true);

                    if (response) {
                        setCheckerData(response.checkerData.data);
                    }
                },
                onError: (error) => {
                    console.error('Error submitting picks:', error);

                    // Check for token expiration (401 Unauthorized)
                    if (error?.response?.status === 401) {
                        dispatch(userLogoutAction({
                            logoutType: 'token-expired'
                        }));
                        return;
                    }

                    const errorMessage = error?.response?.data?.message || 'An error occurred while submitting your picks. Please try again.';
                    setErrorMessage(errorMessage);
                    setErrorModalOpen(true);
                    setSubmitBarOpen(false);
                    setUnsavedPicks(true);
                }
            });
        } catch (error) {
            console.error("Error saving picks: ", error);
            setErrorMessage("An unexpected error occurred");
            setErrorModalOpen(true);
            setSubmitBarOpen(false);
            setUnsavedPicks(true);
        }
    }, [pickState, originalPicks, masterPoolId, myUserId, pickIndex, interval, hasSubmitted, saveTiersPicks, setUnsavedPicks, setSubmitBarOpen, dispatch]);

    // Check if interval param exists in URL
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const intervalParam = queryParams.get('interval');

        // If interval exists in context but not in URL, add it to URL
        if (interval && !intervalParam) {
            queryParams.set('interval', interval);
            navigate({
                pathname: location.pathname,
                search: queryParams.toString()
            }, { replace: true }); // Use replace to avoid adding to history stack
        }
    }, [interval, location.search, navigate, location.pathname]);

    // Memoize filtered tiers data
    const thisIntervalsTiersData = useMemo(() =>
        tiersData?.find((tiersData) => Number(tiersData.interval) === Number(interval)),
        [tiersData, interval]
    );

    // Memoize picks calculation
    const picksToMakeThisInterval = useMemo(() =>
        thisIntervalsTiersData?.tiers?.reduce((acc, tier) => acc + tier.picks, 0),
        [thisIntervalsTiersData]
    );

    // This intervals deadline
    const thisIntervalsInfo = useMemo(() =>
        intervalInfo?.info?.find((info) => Number(info.interval) === Number(interval)),
        [intervalInfo, interval]
    );
    const thisIntervalsDeadlineInNewYork = DateTime.fromISO(thisIntervalsInfo?.deadline, { zone: 'America/New_York' });
    const formattedDeadlineTime = shortDayDateTimFormat(thisIntervalsInfo?.deadline);
    const timeInNewYorkNow = DateTime.now().setZone('America/New_York');
    const hasDeadlinePassed = thisIntervalsDeadlineInNewYork < timeInNewYorkNow;

    // Add pick count data to players in tiers
    const enhancedTiersData = useMemo(() => {
        if (!thisIntervalsTiersData?.tiers?.length || !hasDeadlinePassed) return thisIntervalsTiersData;

        // Create a deep copy of the tiers data
        const enhancedTiers = JSON.parse(JSON.stringify(thisIntervalsTiersData));

        // Add pick count to each player
        enhancedTiers.tiers = enhancedTiers.tiers.map(tier => {
            if (!tier.players) return tier;

            tier.players = tier.players.map(player => {
                // Add pick count
                player.pickCount = countPlayerPicks(player.id);
                return player;
            });

            return tier;
        });

        return enhancedTiers;
    }, [thisIntervalsTiersData, countPlayerPicks, hasDeadlinePassed]);

    const handleSelection = useCallback((interval, tierNumber, tierPicks, id) => {
        // Check if this player is already selected in any tier for this interval
        const pickAlreadySelected = pickState?.some(pick =>
            pick.interval === interval && pick.id === id
        );

        if (pickAlreadySelected) {
            // If player is already selected, just return (prevent selecting the same player twice)
            return;
        }

        // Count how many picks already exist for this tier
        const currentPicksForTier = pickState?.filter(pick =>
            pick.interval === interval && pick.tier === tierNumber
        ).length;

        // Check if we've reached the maximum picks for this tier
        if (currentPicksForTier >= tierPicks) {
            // We need to remove the oldest pick for this tier
            const pickIndexToRemove = pickState?.findIndex(pick =>
                pick.interval === interval && pick.tier === tierNumber
            );

            if (pickIndexToRemove !== -1) {
                const newPickState = [...pickState];
                newPickState.splice(pickIndexToRemove, 1);
                setPickState([...newPickState, { interval, tier: tierNumber, id }]);
            }
        } else {
            // Add the new pick (we haven't reached the maximum yet)
            setPickState([...pickState, { interval, tier: tierNumber, id }]);
        }
    }, [pickState, setPickState]);


    // Use Effect to see if orignalPicks is different from pickState
    useEffect(() => {
        if (originalPicks && pickState) {
            const picksAreEqual =
                originalPicks?.length === pickState?.length &&
                originalPicks?.every(originalPick =>
                    pickState?.some(pick => isEqual(originalPick, pick))
            );

            setUnsavedPicks(!picksAreEqual);
            setSubmitBarOpen(!picksAreEqual);
        }
    }, [originalPicks, pickState, setUnsavedPicks, setSubmitBarOpen]);

    const cardColors = useMemo(() => ({
        backgroundColor: thisIntervalsTiersData?.backgroundColor,
        textColor: thisIntervalsTiersData?.textColor
    }), [thisIntervalsTiersData]);
    //console.log("INTERVAL OPTIONS", intervalOptions);
    // Memoize the tier cards rendering
    const tierCards = useMemo(() => {
        if (!enhancedTiersData?.tiers?.length) return null;

        return enhancedTiersData.tiers.map((tier, index) => (
            <TiersCard
                key={index}
                tier={tier}
                backgroundColor={cardColors?.backgroundColor}
                textColor={cardColors?.textColor}
                handleSelection={handleSelection}
                interval={interval}
                intervalInfo={intervalInfo}
                selectedPlayers={pickState}
                lastCard={index === enhancedTiersData.tiers.length - 1}
                hasDeadlinePassed={hasDeadlinePassed}
            />
        ));
    }, [enhancedTiersData, cardColors, handleSelection, interval, pickState, hasDeadlinePassed, intervalInfo]);
    const modalHandler = () => {
        setSuccessModalOpen(false);
        setTiebreakerAdModal(true);
    }
    return (
        <>
            <TiersDisplayHandler league={league} />

            {!hasDeadlinePassed && (
                <PickDeadline
                    gameTime={thisIntervalsDeadlineInNewYork}
                    customBackgroundColor={"#000000"}
                    customTextColor={"#fff"}
                />
            )}

            {intervalOptions && intervalOptions.length > 0 && (
                <IntervalSelectionButtons
                    selectedInterval={interval}
                    intervals={intervalOptions}
                />
            )}

            {(!thisIntervalsTiersData || !thisIntervalsTiersData.tiers || thisIntervalsTiersData.tiers.length === 0) && (
                <QuickPickGamesComing
                    page={'my-picks'}
                    title={tiersComingTitle}
                    body={tiersComingBody}
                    quickPick={false}
                />
            )}

            {thisIntervalsTiersData && thisIntervalsTiersData.tiers && thisIntervalsTiersData.tiers.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        padding: '16px 32px',
                        maxWidth: '500px',
                        margin: '0 auto',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: isMobile ? '14px' : '18px',
                            fontWeight: 700,
                            color: '#002129',
                            textAlign: 'center',
                            margin: '0 auto',
                        }}
                    >
                        {oneIntervalEvent ? "Choose one golfer per tier." : "Choose one golfer per tier. Once you choose a golfer, you cannot use them in a future Major."}
                    </Typography>
                </Box>
            )}

            {tierCards}

            {successModalOpen && (
                <TiersSubmitModal
                    open={successModalOpen}
                    handleClose={() => {
                        if (league === 'GOLF') {
                            modalHandler();
                        }else{
                            setSuccessModalOpen(false);
                            window.location.reload();
                        }
                    }}
                    checkerData={checkerData}
                    league={league}
                    modalHandler={modalHandler}
                />
            )}

            {tiebreakerAdModal && (
                <TiebreakerAdModal
                    open={tiebreakerAdModal}
                    handleClose={() => {
                        setTiebreakerAdModal(false);
                    }}
                />
            )}

            {errorModalOpen && (
                <ResponseModal
                    open={errorModalOpen}
                    onClose={() => setErrorModalOpen(false)}
                    status="error"
                    message={errorMessage}
                />
            )}

            {submitBarOpen && (
                <QuickPicksSubmitBar
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                    usingTiebreaker={false}
                    picksThisInterval={picksToMakeThisInterval}
                    hasSubmitted={hasSubmitted}
                    league={league}
                />
            )}
        </>
    );
};

export default TiersMyPicks;